// Step 5 of the client-side X12 pipeline (845 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header, Name loops, PAD-style
// price-authorization-detail loops (each nesting its own CTP and QTY), and
// a CTT/AMT summary. Only ever runs against a transaction whose ST01 is
// "845"; anything else is left alone so it can still be shown, read-only,
// by the UI. Unlike the 844, the 845's PAD loop never nests its own Name
// sub-loop and REF/DTM are header-only (never repeated inside a PAD line),
// and the 845 does carry a summary (CTT + AMT), at full parity with
// 850/852/855. Mirrors parser844.ts's pattern, plus a summary phase
// borrowed from parser855.ts.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header845 {
  bpa?: EdiSegment;
  con?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  dtm: EdiSegment[];
  msg: EdiSegment[];
  n9: EdiSegment[];
  other: EdiSegment[];
}

export interface NameLoop845 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemLoop845 {
  pad: EdiSegment;
  ctp?: EdiSegment;
  qty: EdiSegment[];
  other: EdiSegment[];
}

export interface Summary845 {
  ctt?: EdiSegment;
  amt: EdiSegment[];
}

export interface Parsed845 {
  header: Header845;
  nameLoops: NameLoop845[];
  items: ItemLoop845[];
  summary: Summary845;
}

function attachHeaderSegment(header: Header845, s: EdiSegment): void {
  switch (s.id) {
    case 'REF':
      header.refs.push(s);
      break;
    case 'PER':
      header.per = s;
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    default:
      header.other.push(s);
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop845, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemSegment(item: ItemLoop845, s: EdiSegment): void {
  switch (s.id) {
    case 'QTY':
      item.qty.push(s);
      break;
    case 'CTP':
      item.ctp = s;
      break;
    default:
      item.other.push(s);
      break;
  }
}

function emptyHeader(): Header845 {
  return {refs: [], dtm: [], msg: [], n9: [], other: []};
}

function emptyItem(pad: EdiSegment): ItemLoop845 {
  return {pad, qty: [], other: []};
}

type Phase = 'header' | 'name' | 'item' | 'summary';

/**
 * Walks one 845 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is bucketed into that loop's `other` list
 * rather than the walk aborting; the validator is what reports on a
 * document that's missing or misordered.
 */
export function parse845Segments(segments: EdiSegment[]): Parsed845 {
  const header = emptyHeader();
  const nameLoops: NameLoop845[] = [];
  const items: ItemLoop845[] = [];
  const summary: Summary845 = {amt: []};

  let phase: Phase = 'header';
  let currentNameLoop: NameLoop845 | undefined;
  let currentItem: ItemLoop845 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'BPA':
        header.bpa = s;
        break;

      case 'CON':
        header.con = s;
        break;

      case 'MSG':
        header.msg.push(s);
        break;

      case 'N1':
        phase = 'name';
        currentNameLoop = {n1: s, n9: [], refs: [], other: []};
        nameLoops.push(currentNameLoop);
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
        if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'N9':
        if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'header') {
          header.n9.push(s);
        }
        break;

      case 'PAD':
        phase = 'item';
        currentNameLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'CTT':
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        break;

      case 'AMT':
        summary.amt.push(s);
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        }
        break;
    }
  }

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed845 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "845" — it keeps only
   * its raw segment list, for read-only display. */
  parsed845?: Parsed845;
}

export interface EdiGroupWithParsed845 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed845[];
}

export interface EdiEnvelopeWithParsed845 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed845[];
}

/**
 * Parses every 845 transaction in an envelope tree (step 2's output),
 * attaching `parsed845` to each. A non-845 transaction is passed through
 * unchanged, `parsed845` left undefined.
 */
export function attachParsed845(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed845 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed845:
          transaction.transactionSetCode === '845'
            ? parse845Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
